import {
  alpha,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { FC, ReactNode } from 'react'

type Option = {
  value: string
  label: string
  icon?: ReactNode
  disabled?: boolean
}

export type Props = {
  label: string
  value: string
  onChange(value: string): void
  options: Option[]
  type: 'radio' | 'dropdown' | 'toggle'
  disabled?: boolean
}

const SelectOne: FC<Props> = (props) => {
  if (props.type === 'radio')
    return (
      <FormControl disabled={props.disabled}>
        <FormLabel id={`${props.label}-label`} sx={{ color: 'text.secondary', mb: 1 }}>
          {props.label}
        </FormLabel>
        <RadioGroup
          aria-labelledby={`${props.label}-label`}
          value={props.value}
          onChange={(event) => props.onChange(event.target.value)}
        >
          {props.options.map((option) => (
            <FormControlLabel
              key={`${props.label}-label-${option.value}`}
              value={option.value}
              control={<Radio />}
              disabled={option.disabled}
              label={option.label}
            />
          ))}
        </RadioGroup>
      </FormControl>
    )
  if (props.type === 'dropdown')
    return (
      <Box sx={{ flexGrow: 1 }}>
        <TextField
          label={props.label}
          fullWidth
          sx={{
            minWidth: (theme) => `min(${theme.fieldWidth.sm}, 100%)`,
            '& .MuiInput-underline:before': {
              borderBottomColor: (theme) => alpha(theme.palette.primary.main, 0.1),
            },
          }}
          select
          disabled={props.disabled}
          value={props.value}
          onChange={(event) => props.onChange(event.target.value)}
          variant="standard"
        >
          {props.options.map((option) => (
            <MenuItem key={`${props.label}-label-${option.value}`} value={option.value} disabled={option.disabled}>
              {option.icon ? (
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                  {option.icon}
                  {option.label}
                </Stack>
              ) : (
                option.label
              )}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    )

  return (
    <Stack spacing={1}>
      {props.label && (
        <Typography variant="caption" color="text.secondary">
          {props.label}
        </Typography>
      )}

      <ToggleButtonGroup
        value={props.value}
        exclusive
        disabled={props.disabled}
        onChange={(_event, value) => props.onChange(value)}
        size="small"
        sx={{
          minHeight: '2rem',
          width: '100%',
          '& .MuiToggleButton-root': {
            flex: 1,
            minWidth: 0,
            px: { xs: 0.5, sm: 1.5 },
            textTransform: 'none',
            typography: 'body2',
          },
        }}
      >
        {props.options.map((option) => (
          <ToggleButton key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  )
}

export default SelectOne
